'use client';
import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ServiceCardProps {
  icon: string
  title: string
  description: string
  specs: string[]
  delay?: number
}

export default function ServiceCard({ 
  icon, 
  title, 
  description, 
  specs, 
  delay = 0 
}: ServiceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div 
      className="bento-card bg-card border border-border rounded-lg group reveal"
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Mobile: tappable header to expand/collapse. Desktop: static header */}
      <div className="w-full flex items-start justify-between p-4 md:p-8 text-left">
        <div className="flex items-start gap-4 min-w-0">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
            <Icon 
              name={icon as any}
              size={28}
              className="text-primary"
            />
          </div>
          <div className="min-w-0">
            <h3 className="text-xl font-bold text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground mt-0.5 leading-snug">{description}</p>
          </div>
        </div>
        {/* Chevron toggle — only visible on mobile */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="md:hidden ml-4 flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center transition-transform duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
        >
          <Icon 
            name="ChevronDownIcon"
            size={16}
            className="text-muted-foreground"
          />
        </button>
      </div>

      {/* Specs: always visible on desktop, collapsible on mobile */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out md:max-h-none md:opacity-100 ${
          isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 md:px-8 pb-4 md:pb-8 pt-0">
          <div className="border-t border-border pt-4 space-y-2">
            <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
              Technical Specifications
            </h4>
            {specs.map((spec, index) => (
              <div 
                key={`spec_${index}_${spec.substring(0, 10)}`}
                className="flex items-start gap-2"
              >
                <Icon 
                  name="CheckCircleIcon"
                  size={16}
                  className="text-success mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-muted-foreground">{spec}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}